import {
  BaseUser,
  EntitlementType,
  OrgScopedBaseUser,
  User,
} from "src/stores/models/user";
import {
  QualityEventAnswers,
  QualityEventDraft,
  QualityEventField,
  QualityEventProposal,
  QualityEventRecord,
  QualityEventStep,
  isQualityEventApproval,
  qualityEventRows,
} from "src/types/quality-events";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";

const proposalAnswers = (
  fields: QualityEventField[],
  answers: QualityEventProposal["answers"],
  users: BaseUser[],
  previous: QualityEventAnswers
): QualityEventAnswers => {
  const result: QualityEventAnswers = {};
  const userIds = new Set(users.map((user) => user.id));
  for (const field of fields) {
    const value = answers[field.key];
    if (value === undefined) continue;
    if (field.type === "user") {
      if (value === null || (typeof value === "string" && userIds.has(value)))
        result[field.key] = value;
    } else if (field.type === "approval") {
      if (value && typeof value === "object" && "approver" in value) {
        if (value.approver === null || userIds.has(value.approver))
          result[field.key] = {
            approver: value.approver,
            dueDate: value.dueDate ?? null,
          };
      } else result[field.key] = null;
    } else if (field.type === "repeatable") {
      // A value for another field type must not erase existing rows.
      if (value === null) result[field.key] = [];
      else if (
        Array.isArray(value) &&
        value.every((row) => typeof row === "object")
      ) {
        const previousRows = qualityEventRows(previous[field.key]);
        result[field.key] = value.map((row) => {
          const nextRow: { id: string; answers: QualityEventAnswers } = {
            id: row.id ?? crypto.randomUUID(),
            answers: { ...row.answers },
          };
          const previousRow = previousRows.find((item) => item.id === row.id);
          for (const [key, answer] of Object.entries(row.answers)) {
            const child = field.fields.find((child) => child.key === key);
            if (
              child?.type !== "user" ||
              answer === null ||
              users.some((user) => user.id === answer)
            )
              continue;
            // Unknown suggested users must not erase a saved assignment.
            const previousAnswer = previousRow?.answers[key];
            if (previousAnswer === undefined) delete nextRow.answers[key];
            else nextRow.answers[key] = previousAnswer;
          }
          return nextRow;
        });
      }
    } else if (
      value === null ||
      typeof value !== "object" ||
      (Array.isArray(value) && value.every((item) => typeof item === "string"))
    )
      result[field.key] = value;
  }
  return result;
};

export const getQualityEventDraft = ({
  step,
  record,
  proposal,
  users = [],
  assignment,
  creatorId,
}: {
  step: QualityEventStep;
  record?: QualityEventRecord;
  proposal?: QualityEventProposal;
  users?: BaseUser[];
  assignment?: QualityEventRecord["definition"]["definition"]["assignment"];
  creatorId?: string;
}): QualityEventDraft => {
  const existingStep = record?.steps[step.key];
  const answers = { ...existingStep?.answers };
  for (const field of step.fields) {
    const value = answers[field.key];
    if (field.type === "approval" && isQualityEventApproval(value))
      answers[field.key] = { approver: value.approver, dueDate: value.dueDate };
  }
  const draftAnswers = {
    ...answers,
    ...proposalAnswers(
      step.fields.filter((field) => record || field.type !== "approval"),
      proposal?.answers ?? {},
      users,
      answers
    ),
  };
  if (
    !record &&
    creatorId &&
    assignment?.stepKey === step.key &&
    draftAnswers[assignment.assigneeField] == null
  )
    draftAnswers[assignment.assigneeField] = creatorId;
  return {
    id: record?.id ?? proposal?.id ?? crypto.randomUUID(),
    expectedVersion: proposal?.expectedVersion ?? record?.version,
    title: proposal?.title ?? record?.title ?? "",
    answers: draftAnswers,
    files: {},
  };
};
export const isQualityEventStepUnlocked = (
  record: QualityEventRecord,
  stepKey: string
) => {
  const steps = record.definition.definition.steps;
  const index = steps.findIndex((step) => step.key === stepKey);
  const currentIndex = steps.findIndex(
    (step) => step.key === record.currentStepKey
  );
  return record.status !== "CLOSED" && index >= 0 && index <= currentIndex;
};

export const canEditQualityEventRecord = (
  record: QualityEventRecord,
  user:
    | (Pick<User, "id" | "permissions"> &
        Pick<OrgScopedBaseUser, "organizationMemberships">)
    | undefined
) => {
  if (record.status === "CLOSED" || !user) return false;
  const editing = record.definition.definition.editing;
  if (!editing) return true;
  const assignment = record.definition.definition.assignment;
  return !!(
    (editing.recordOwner && record.createdBy.id === user.id) ||
    (editing.recordResponsible &&
      ((assignment &&
        record.steps[assignment.stepKey]?.answers[assignment.assigneeField] ===
          user.id) ||
        record.definition.definition.steps.some((step) =>
          step.fields.some((field) => {
            if (field.type !== "repeatable" || !field.task) return false;
            const { assigneeField } = field.task;
            return qualityEventRows(
              record.steps[step.key].answers[field.key]
            ).some((row) => row.answers[assigneeField] === user.id);
          })
        ))) ||
    user.permissions?.includes(editing.adminPermission) ||
    user.organizationMemberships?.some(
      (membership) =>
        membership.organizationId === record.organizationId &&
        membership.activated &&
        hasFunctionalGroupEntitlement(
          user,
          record.organizationId,
          EntitlementType.ADMINISTER_ALL_TRAININGS
        )
    )
  );
};

export const prepareQualityEventChanges = async (
  drafts: Record<string, QualityEventDraft>,
  stepKey: string,
  firstStepKey: string,
  prepare: (draft: QualityEventDraft) => Promise<{
    title: string;
    answers: QualityEventAnswers;
  }>
) => {
  const prepared = await Promise.all(
    Object.entries(drafts).map(
      async ([key, draft]) => [key, await prepare(draft)] as const
    )
  );
  const values = Object.fromEntries(prepared);
  const steps = Object.fromEntries(
    prepared
      .filter(([key]) => key !== stepKey)
      .map(([key, { title: _title, ...fields }]) => [key, fields])
  );
  return {
    ...values[stepKey],
    title: drafts[firstStepKey]?.title ?? drafts[stepKey].title,
    ...(Object.keys(steps).length > 0 && { steps }),
  };
};
